import { computed, inject, Injectable, signal } from '@angular/core';

import type {
  ProductCategory,
  ProductUpdate,
  ProductUpdatesQueryParams,
  UpdateCategory,
} from '@product-updates/shared-types';
import { type MainTab, PRODUCT_MAP, type ProductTab } from '@product-updates/shared-util';
import { catchError, of, tap } from 'rxjs';

import { ProductUpdatesHttpService } from './product-updates-http.service';
import {
  PRODUCT_UPDATES_CONFIG,
  type ProductUpdatesConfig,
} from '../config/product-updates-config.provider';

interface CachedData {
  data: ProductUpdate[];
  totalCount: number;
  currentPage: number;
}

@Injectable({ providedIn: 'root' })
export class ProductUpdatesStateService {
  private readonly httpService = inject(ProductUpdatesHttpService);
  private readonly config = inject<ProductUpdatesConfig>(PRODUCT_UPDATES_CONFIG);

  private readonly currentPage = signal(1);
  private readonly totalCount = signal(0);
  private readonly perPage = 15;
  private readonly dataCache = new Map<string, CachedData>();
  private currentRequestId = 0;

  private getCacheKey(mainTab: MainTab, productTab: ProductTab): string {
    return `${mainTab}:${productTab}:${this.currentLocale()}`;
  }

  /* eslint-disable @typescript-eslint/member-ordering */
  public readonly productUpdates = signal<ProductUpdate[]>([]);
  public readonly loading = signal(true);
  public readonly loadingMore = signal(false);
  public readonly error = signal<string | null>(null);
  public readonly currentArticle = signal<ProductUpdate | null>(null);
  public readonly isArticlePage = signal(false);
  public readonly activeMainTab = signal<MainTab>('whats-new');
  public readonly activeProductTab = signal<ProductTab>('all');
  public readonly selectedProductCategories = signal<ProductCategory[]>([]);
  public readonly selectedUpdateCategories = signal<UpdateCategory[]>([]);
  public readonly currentLocale = signal(this.detectInitialLocale());

  // Timeline mobile breadcrumb state (set by timeline component)
  public readonly timelineQuarterLabel = signal<string | null>(null);
  public readonly timelineMonthLabel = signal<string | null>(null);

  // Changelog visibility (only show tab if posts exist)
  public readonly hasChangelogPosts = signal<boolean>(false);

  public readonly hasMore = computed(() => this.productUpdates().length < this.totalCount());
  public readonly filteredUpdates = computed(() => this.productUpdates());
  public readonly hasActiveFilters = computed(
    () => this.selectedProductCategories().length > 0 || this.selectedUpdateCategories().length > 0,
  );
  public readonly availableLocales = computed(() => this.config.availableLocales);

  public readonly backRoute = computed(() => {
    const tab = this.activeMainTab();
    if (tab === 'coming-soon') return '/coming-soon';
    if (tab === 'timeline') return '/timeline';
    if (tab === 'changelog') return '/changelog';
    return '/';
  });

  public readonly backQueryParams = computed(() => {
    const params: Record<string, string> = {};
    if (this.activeProductTab() !== 'all') params['product'] = this.activeProductTab();
    if (this.currentLocale() !== this.config.defaultLocale) params['lang'] = this.currentLocale();
    return params;
  });

  public loadProductUpdates(forceReload = false): void {
    const cacheKey = this.getCacheKey(this.activeMainTab(), this.activeProductTab());
    const requestId = ++this.currentRequestId;
    const cached = this.dataCache.get(cacheKey);

    if (!forceReload && cached !== undefined) {
      this.productUpdates.set(cached.data);
      this.totalCount.set(cached.totalCount);
      this.currentPage.set(cached.currentPage);
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.currentPage.set(1);

    this.httpService
      .fetchProductUpdatesPaginated(this.buildQueryParams(1))
      .pipe(
        tap(response => {
          const data = this.excludeComingSoon(response.data);
          const totalCount = response.pagination?.totalEntries ?? data.length;
          this.dataCache.set(cacheKey, { data, totalCount, currentPage: 1 });
          if (requestId === this.currentRequestId) {
            this.productUpdates.set(data);
            this.totalCount.set(totalCount);
            this.currentPage.set(1);
            this.loading.set(false);
          }
        }),
        catchError((err: unknown) => {
          if (requestId === this.currentRequestId) {
            this.error.set('Failed to load product updates. Please try again.');
            this.loading.set(false);
          }
          console.error('Error loading product updates:', err);
          return of({
            data: [],
            pagination: { page: 1, pageSize: this.perPage, totalEntries: 0, totalPages: 0 },
          });
        }),
      )
      .subscribe();
  }

  private buildQueryParams(page: number): ProductUpdatesQueryParams {
    const params: ProductUpdatesQueryParams = {
      locale: this.currentLocale(),
      page,
      perPage: this.perPage,
    };
    const productTab = this.activeProductTab();
    // AI tab shows all AI posts regardless of main tab
    if (productTab === 'ai') {
      params.productCategories = [PRODUCT_MAP[productTab]];
      return params;
    }
    const mainTab = this.activeMainTab();
    if (mainTab === 'coming-soon') params.updateCategories = ['Coming soon'];
    else if (mainTab === 'changelog') params.updateCategories = ['Changelog'];
    else if (mainTab === 'whats-new') params.updateCategories = ['Feature', 'Improvement', 'New'];
    if (productTab !== 'all') params.productCategories = [PRODUCT_MAP[productTab]];
    return params;
  }

  public loadAllProductUpdates(): void {
    const currentProductTab = this.activeProductTab();
    const cacheKey = this.getCacheKey('timeline', currentProductTab);
    const requestId = ++this.currentRequestId;
    const cached = this.dataCache.get(cacheKey);

    if (cached !== undefined) {
      this.productUpdates.set(cached.data);
      this.totalCount.set(cached.totalCount);
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    const params: ProductUpdatesQueryParams = {
      locale: this.currentLocale(),
      page: 1,
      perPage: 1000,
    };
    if (currentProductTab !== 'all') params.productCategories = [PRODUCT_MAP[currentProductTab]];

    this.httpService
      .fetchProductUpdatesPaginated(params)
      .pipe(
        tap(response => {
          const data = response.data;
          const totalCount = response.pagination?.totalEntries ?? data.length;
          this.dataCache.set(cacheKey, { data, totalCount, currentPage: 1 });
          if (requestId === this.currentRequestId) {
            this.productUpdates.set(data);
            this.totalCount.set(totalCount);
            this.loading.set(false);
          }
        }),
        catchError((err: unknown) => {
          if (requestId === this.currentRequestId) {
            this.error.set('Failed to load product updates. Please try again.');
            this.loading.set(false);
          }
          console.error('Error loading all product updates:', err);
          return of({
            data: [],
            pagination: { page: 1, pageSize: 1000, totalEntries: 0, totalPages: 0 },
          });
        }),
      )
      .subscribe();
  }

  public checkChangelogExists(): void {
    // Make a lightweight API call to check if any changelog posts exist
    const params: ProductUpdatesQueryParams = {
      locale: this.currentLocale(),
      page: 1,
      perPage: 1,
      updateCategories: ['Changelog'],
    };

    this.httpService
      .fetchProductUpdatesPaginated(params)
      .pipe(
        tap(response => {
          const totalCount = response.pagination?.totalEntries ?? response.data.length;
          this.hasChangelogPosts.set(totalCount > 0);
        }),
        catchError(() => {
          this.hasChangelogPosts.set(false);
          return of({
            data: [],
            pagination: { page: 1, pageSize: 1, totalEntries: 0, totalPages: 0 },
          });
        }),
      )
      .subscribe();
  }

  public loadMoreProductUpdates(): void {
    if (this.loadingMore() || this.loading() || !this.hasMore()) return;

    const nextPage = this.currentPage() + 1;
    const cacheKey = this.getCacheKey(this.activeMainTab(), this.activeProductTab());
    this.loadingMore.set(true);
    const requestId = ++this.currentRequestId;

    this.httpService
      .fetchProductUpdatesPaginated(this.buildQueryParams(nextPage))
      .pipe(
        tap(response => {
          if (requestId === this.currentRequestId) {
            const newData = [...this.productUpdates(), ...this.excludeComingSoon(response.data)];
            const totalCount = response.pagination?.totalEntries ?? this.totalCount();
            this.productUpdates.set(newData);
            this.currentPage.set(nextPage);
            this.totalCount.set(totalCount);
            this.dataCache.set(cacheKey, { data: newData, totalCount, currentPage: nextPage });
          }
          this.loadingMore.set(false);
        }),
        catchError((err: unknown) => {
          console.error('Error loading more product updates:', err);
          this.loadingMore.set(false);
          return of({
            data: [],
            pagination: {
              page: nextPage,
              pageSize: this.perPage,
              totalEntries: this.totalCount(),
              totalPages: Math.ceil(this.totalCount() / this.perPage),
            },
          });
        }),
      )
      .subscribe();
  }

  public setLocale(locale: string, reload = true): void {
    if (!this.config.availableLocales.includes(locale)) return;
    this.currentLocale.set(locale);
    if (reload) this.loadProductUpdates();
  }

  public setMainTab(tab: MainTab): void {
    this.activeMainTab.set(tab);
  }

  public setProductTab(tab: ProductTab): void {
    this.activeProductTab.set(tab);
    this.activeMainTab() === 'timeline' ? this.loadAllProductUpdates() : this.loadProductUpdates();
  }

  public toggleProductCategory(category: ProductCategory): void {
    this.selectedProductCategories.update(c =>
      c.includes(category) ? c.filter(x => x !== category) : [...c, category],
    );
  }

  public toggleUpdateCategory(category: UpdateCategory): void {
    this.selectedUpdateCategories.update(c =>
      c.includes(category) ? c.filter(x => x !== category) : [...c, category],
    );
  }

  public setProductCategory(category: ProductCategory | null): void {
    this.selectedProductCategories.set(category === null ? [] : [category]);
  }
  public clearFilters(): void {
    this.selectedProductCategories.set([]);
    this.selectedUpdateCategories.set([]);
  }
  public setCurrentArticle(article: ProductUpdate | null): void {
    this.currentArticle.set(article);
  }
  public setIsArticlePage(isArticle: boolean): void {
    this.isArticlePage.set(isArticle);
  }

  public setTimelineBreadcrumb(quarterLabel: string | null, monthLabel: string | null): void {
    this.timelineQuarterLabel.set(quarterLabel);
    this.timelineMonthLabel.set(monthLabel);
  }

  public getArticleBySlug(slug: string): ProductUpdate | undefined {
    return this.productUpdates().find(a => {
      const t = a.translations.find(x => x.locale === a.primaryLocale) ?? a.translations[0];
      return t?.slug === slug;
    });
  }
  /* eslint-enable @typescript-eslint/member-ordering */

  private excludeComingSoon(data: ProductUpdate[]): ProductUpdate[] {
    if (this.activeMainTab() === 'coming-soon') return data;
    return data.filter(post => !post.updateCategories.includes('Coming soon'));
  }

  private detectInitialLocale(): string {
    const lang = navigator.language?.split('-')[0] || this.config.defaultLocale;
    return this.config.availableLocales.includes(lang) ? lang : this.config.defaultLocale;
  }
}
